import React, { useState } from "react";
import * as finService from "../../../services/finService";
import MasterSection from "../../Platform/setup/MasterSection";
import useFinOptions, { optionByCode } from "../useFinOptions";
import useGlContext from "../gl/glContext";
import AccountPicker from "../gl/AccountPicker";
import { act, hint, linkBtn } from "../gl/glUi";
import { PrimaryButton, SecondaryButton, SlidePanel, Tabs, useLoad } from "../../../components/ui";

// Finance Setup → Cash & bank: bank accounts and their GL accounts, how customers can pay and
// where (Collection Setup), cash drawers and petty cash funds.
const m = (key) => ({ load: () => finService.listMaster(key), save: (id, data) => finService.saveMaster(key, id, data) });
const CODE = { key: "code", label: "Code", required: true, max: 16 };
const NAME = { key: "name", label: "Name", required: true, max: 100 };
const TYPES = [
  ["CASH", "Cash (through a cash drawer)"],
  ["CARD", "Card"],
  ["BANK_TRANSFER", "Bank transfer"],
  ["CHEQUE", "Cheque (current)"],
  ["PDC", "Post-dated cheque"],
  ["ONLINE", "Online payment"],
].map(([value, label]) => ({ value, label }));

function BankGl({ bank, onHide }) {
  const gl = useGlContext();
  const [acct, setAcct] = useState(null);
  const save = async () => (await act(() => finService.setBankGlAccount(bank.id, acct))) && onHide();
  return (
    <SlidePanel show onHide={onHide} title={`GL account – ${bank.name}`} width={560} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={!acct || !acct.ACCOUNT}>Save</PrimaryButton></>}>
      <div style={{ ...hint, marginBottom: 12 }}>Money in and out of this bank posts here. Without one, the BANK line type's default account is used.</div>
      {gl.structure ? <AccountPicker id="bank-gl" structure={gl.structure} companyValue={gl.ledger ? gl.ledger.company_value : null} value={acct} onChange={setAcct} /> : null}
    </SlidePanel>
  );
}

export default function CashSetup() {
  const [section, setSection] = useState("banks");
  const [changed, setChanged] = useState(0);
  const opts = useFinOptions(["legal-entities", "bank-accounts", "locations", "receipt-methods", "currencies"], changed);
  const gls = useLoad(() => finService.listBankGlAccounts(), [changed]);
  const [glFor, setGlFor] = useState(null);
  const ccy = (opts.currencies || []).map((o) => ({ label: o.label, value: o.code }));
  const bump = () => setChanged((n) => n + 1);
  return (
    <>
      <Tabs
        active={section}
        onChange={setSection}
        tabs={[
          { key: "banks", label: "Bank accounts" },
          { key: "methods", label: "Receipt methods" },
          { key: "collection", label: "Collection setup" },
          { key: "drawers", label: "Cash drawers" },
          { key: "petty", label: "Petty cash funds" },
          { key: "rules", label: "Statement rules" },
        ]}
      />
      {section === "rules" ? (
        <MasterSection
          {...m("bank-rec-rules")}
          noun="statement rule"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Statement text contains", (r) => r.match_text], ["Book to account", (r) => r.account_value]]}
          fieldKeys={["name", "match_text", "account_value"]}
          lockedOnEdit={["code"]}
          fields={() => [CODE, NAME, { key: "match_text", label: "Statement text contains", required: true, max: 100, hint: "e.g. BANK CHARGES, INTEREST" }, { key: "account_value", label: "Book to account (ACCOUNT value)", required: true, max: 25 }]}
        />
      ) : null}
      {section === "banks" ? (
        <MasterSection
          {...m("bank-accounts")}
          noun="bank account"
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Bank", (r) => r.bank_name], ["IBAN", (r) => r.iban], ["Currency", (r) => r.currency], ["GL account", (r) => ((gls.data || []).find((g) => g.code === r.code) || {}).account || "Default"]]}
          fieldKeys={["name", "bank_name", "account_number", "iban", "swift"]}
          lockedOnEdit={["code", "currency", "legal_entity_id"]}
          defaults={{ currency: "AED" }}
          toForm={(r) => ({ ...r, legal_entity_id: optionByCode(opts["legal-entities"], r.legal_entity_code) })}
          extraActions={(r) => (
            <>
              <button type="button" style={linkBtn} onClick={() => setGlFor(r)}>
                GL account
              </button>{" "}
            </>
          )}
          fields={() => [
            { key: "legal_entity_id", label: "Legal entity", type: "select", required: true, options: opts["legal-entities"] || [] },
            CODE,
            NAME,
            { key: "bank_name", label: "Bank", required: true, max: 100 },
            { key: "account_number", label: "Account number", max: 40 },
            { key: "iban", label: "IBAN", max: 34 },
            { key: "swift", label: "SWIFT", max: 11 },
            { key: "currency", label: "Currency", type: "select", required: true, options: ccy },
          ]}
        />
      ) : null}
      {section === "methods" ? (
        <MasterSection
          {...m("receipt-methods")}
          noun="receipt method"
          deps={[changed]}
          onChanged={bump}
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Type", (r) => (TYPES.find((t) => t.value === r.method_type) || {}).label], ["Bank account", (r) => r.bank_account_name || "—"]]}
          fieldKeys={["name", "bank_account_id"]}
          lockedOnEdit={["code", "method_type"]}
          defaults={{ method_type: "CASH" }}
          toForm={(r) => ({ ...r, bank_account_id: optionByCode(opts["bank-accounts"], r.bank_account_code) })}
          fields={() => [CODE, NAME, { key: "method_type", label: "Type", type: "select", required: true, options: TYPES }, { key: "bank_account_id", label: "Lands in bank account", type: "select", options: opts["bank-accounts"] || [] }]}
        />
      ) : null}
      {section === "collection" ? (
        <>
          <div style={{ ...hint, marginBottom: 12 }}>A location with no methods listed accepts every method; list them to restrict what a cashier there can take.</div>
          <MasterSection
            {...m("receipt-method-locations")}
            noun="method at a location"
            deps={[changed]}
            columns={[["Location", (r) => r.location_name], ["Method", (r) => r.method_name]]}
            fieldKeys={[]}
            lockedOnEdit={["location_id", "method_id"]}
            canEdit
            fields={() => [
              { key: "location_id", label: "Location", type: "select", required: true, options: opts.locations || [] },
              { key: "method_id", label: "Receipt method", type: "select", required: true, options: opts["receipt-methods"] || [] },
            ]}
          />
        </>
      ) : null}
      {section === "drawers" ? (
        <MasterSection
          {...m("cash-drawers")}
          noun="cash drawer"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Location", (r) => r.location_name], ["Currency", (r) => r.currency]]}
          fieldKeys={["name", "location_id"]}
          lockedOnEdit={["code", "currency"]}
          defaults={{ currency: "AED" }}
          toForm={(r) => ({ ...r, location_id: optionByCode(opts.locations, r.location_code) })}
          fields={() => [CODE, NAME, { key: "location_id", label: "Location", type: "select", required: true, options: opts.locations || [] }, { key: "currency", label: "Currency", type: "select", required: true, options: ccy }]}
        />
      ) : null}
      {section === "petty" ? (
        <MasterSection
          {...m("petty-cash-funds")}
          noun="petty cash fund"
          columns={[["Code", (r) => r.code], ["Name", (r) => r.name], ["Location", (r) => r.location_name], ["Custodian", (r) => r.custodian_employee_name], ["Float", (r) => Number(r.float_amount)], ["Balance", (r) => Number(r.balance)]]}
          fieldKeys={["name", "location_id", "custodian_employee_id", "float_amount"]}
          lockedOnEdit={["code", "currency"]}
          defaults={{ currency: "AED" }}
          toForm={(r) => ({ ...r, location_id: optionByCode(opts.locations, r.location_code), float_amount: Number(r.float_amount) })}
          fields={() => [
            CODE,
            NAME,
            { key: "location_id", label: "Location", type: "select", required: true, options: opts.locations || [] },
            { key: "custodian_employee_id", label: "Custodian", type: "employee", required: true },
            { key: "currency", label: "Currency", type: "select", required: true, options: ccy },
            { key: "float_amount", label: "Float (top it up from Cash & Bank)", type: "number", step: "any", min: 0, required: true },
          ]}
        />
      ) : null}
      {glFor ? (
        <BankGl
          bank={glFor}
          onHide={() => {
            setGlFor(null);
            bump();
          }}
        />
      ) : null}
    </>
  );
}
